import React from 'react';
import { FlexDiv, TextDefault, TextInput } from './ui';
import { Box, Paper, Stack, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    PASSWORD_TESTER_BAND_KEYS,
    getBlockOptionComponent,
    getScoreForBlockOption,
    setBlockOptionComponent,
    setBlockValuesScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
};

const BAND_LABELS = {
    bad: 'Bad',
    almost: 'Almost',
    perfect: 'Perfect',
};

function emptyHeadingRow() {
    return {
        text: '',
        prefix: '',
        scoreFrom: 0,
        scoreTo: 0,
    };
}

/**
 * Copy, answer.*, and flat scores (bad / almost / perfect) for password-tester blocks.
 */
export function PasswordTesterBlockEditor({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    if (selectedBlock?.type !== 'password-tester') {
        return null;
    }

    const answer = selectedBlock.answer || {};
    const heading = Array.isArray(answer.heading) ? answer.heading : [];

    const patchAnswer = (patch) => {
        onUpdateBlock({ answer: { ...answer, ...patch } });
    };

    const patchHeadingRow = (index, rowPatch) => {
        const next = heading.map((row, i) =>
            i === index ? { ...row, ...rowPatch } : row
        );
        patchAnswer({ heading: next });
    };

    const addHeadingRow = () => {
        patchAnswer({ heading: [...heading, emptyHeadingRow()] });
    };

    const removeHeadingRow = (index) => {
        if (heading.length <= 1) return;
        patchAnswer({
            heading: heading.filter((_, i) => i !== index),
        });
    };

    return (
        <FlexDiv
            column
            gap={2}
            $noWrap
            style={{ minWidth: 0, width: '100%' }}
        >
            <TextDefault bold>Password tester</TextDefault>

            <TextDefault bold>Answer (feedback)</TextDefault>
            <TextDefault lighter>
                Heading rows map score ranges to prefix and message text.
            </TextDefault>

            {heading.map((row, index) => (
                <Paper key={index} elevation={0} sx={paperSx}>
                    <Stack spacing={1.5}>
                        <FlexDiv alignCenter gap={8}>
                            <TextDefault bold>
                                Heading row {index + 1}
                            </TextDefault>
                            {heading.length > 1 ? (
                                <IconButton
                                    size="small"
                                    aria-label="Remove row"
                                    onClick={() => removeHeadingRow(index)}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </FlexDiv>
                        <TextField
                            size="small"
                            label="Prefix"
                            value={row.prefix ?? ''}
                            onChange={(e) =>
                                patchHeadingRow(index, {
                                    prefix: e.target.value,
                                })
                            }
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Text"
                            value={row.text ?? ''}
                            onChange={(e) =>
                                patchHeadingRow(index, { text: e.target.value })
                            }
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <FlexDiv gap={8} wrap>
                            <TextField
                                size="small"
                                type="number"
                                label="Score from"
                                value={row.scoreFrom ?? ''}
                                onChange={(e) =>
                                    patchHeadingRow(index, {
                                        scoreFrom: Number(e.target.value),
                                    })
                                }
                                sx={{ minWidth: 120 }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Score to"
                                value={row.scoreTo ?? ''}
                                onChange={(e) =>
                                    patchHeadingRow(index, {
                                        scoreTo: Number(e.target.value),
                                    })
                                }
                                sx={{ minWidth: 120 }}
                            />
                            <TextField
                                size="small"
                                label="Description key (row)"
                                value={row.description_key ?? ''}
                                onChange={(e) =>
                                    patchHeadingRow(index, {
                                        description_key: e.target.value,
                                    })
                                }
                                sx={{ flex: 1, minWidth: 160 }}
                            />
                        </FlexDiv>
                    </Stack>
                </Paper>
            ))}
            <FlexDiv>
                <IconButton
                    size="small"
                    onClick={addHeadingRow}
                    aria-label="Add heading row"
                >
                    <AddIcon />
                </IconButton>
                <TextDefault lighter>Add heading row</TextDefault>
            </FlexDiv>

            <TextField
                size="small"
                label="Answer description_key"
                value={answer.description_key ?? ''}
                onChange={(e) =>
                    patchAnswer({ description_key: e.target.value })
                }
                fullWidth
            />
            <TextField
                size="small"
                label="Review heading text"
                value={answer.reviewHeadingText ?? ''}
                onChange={(e) =>
                    patchAnswer({ reviewHeadingText: e.target.value })
                }
                fullWidth
                multiline
                minRows={2}
            />
            <TextField
                size="small"
                type="number"
                label="Review legend step count"
                value={
                    answer.reviewLegendStepCount != null
                        ? answer.reviewLegendStepCount
                        : ''
                }
                onChange={(e) =>
                    patchAnswer({
                        reviewLegendStepCount: Number(e.target.value),
                    })
                }
                sx={{ maxWidth: 220 }}
            />

            <TextDefault bold>Scores (bad / almost / perfect)</TextDefault>
            <TextDefault lighter>
                Set how many points each strength band is worth.
            </TextDefault>
            {PASSWORD_TESTER_BAND_KEYS.map((band) => {
                const score = getScoreForBlockOption(
                    question.actions,
                    selectedBlock.actionId,
                    band
                );
                const scoreComponent =
                    selectedBlock.actionId && onPatchQuestion
                        ? getBlockOptionComponent(
                              question.actions,
                              selectedBlock.actionId,
                              band,
                              question
                          )
                        : 'behaviour';
                return (
                    <Paper key={band} elevation={0} sx={paperSx}>
                        <Stack spacing={1.5} sx={{ minWidth: 0, width: '100%' }}>
                            <TextDefault bold>{BAND_LABELS[band]}</TextDefault>
                            <TextField
                                size="small"
                                type="number"
                                label="Score"
                                value={score}
                                onChange={(e) => {
                                    if (!onPatchQuestion) return;
                                    onPatchQuestion((prev) => ({
                                        ...prev,
                                        actions: setBlockValuesScore(
                                            prev.actions,
                                            selectedBlock.actionId,
                                            band,
                                            e.target.value,
                                            prev
                                        ),
                                    }));
                                }}
                                sx={{ width: '100%', maxWidth: 220 }}
                            />
                            {onPatchQuestion ? (
                                <Box sx={{ width: '100%', minWidth: 0 }}>
                                    <ScoringComponentSelect
                                        componentOptions={scoringComponentOptions}
                                        value={scoreComponent}
                                        onChange={(next) =>
                                            onPatchQuestion((prev) => ({
                                                ...prev,
                                                actions: setBlockOptionComponent(
                                                    prev.actions,
                                                    selectedBlock.actionId,
                                                    band,
                                                    next,
                                                    prev
                                                ),
                                            }))
                                        }
                                    />
                                </Box>
                            ) : null}
                        </Stack>
                    </Paper>
                );
            })}
        </FlexDiv>
    );
}
